import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Plus, RefreshCw } from 'lucide-react';
import { APPOINTMENT_STATUSES, labelFor, staffLabel, receptionApiErrorMessage } from './receptionConstants';
import { useReceptionStaff } from './useReceptionData';
import MobileInput from '../ui/mobile-input';
import DateTimeInput from '../ui/datetime-input';
import { validateMobile } from '../../utils/phoneValidation';
import { validateDateTime } from '../../utils/dateValidation';

const empty = () => ({
  visitor_name: '', phone: '', staff_user_id: '', appointment_datetime: '', purpose: '', status: 'scheduled',
});

export default function ReceptionAppointments() {
  const { toast } = useToast();
  const { canManageFeature, hasPermission } = useAuth();
  const canManage = canManageFeature('reception') || hasPermission('manage_reception');
  const canDelete = hasPermission('delete_reception');
  const staff = useReceptionStaff();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editRow, setEditRow] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [datetimeError, setDatetimeError] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reception/appointments');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const save = async () => {
    if (!form.visitor_name?.trim() || !form.appointment_datetime) {
      toast({ title: 'Visitor name and appointment time required', variant: 'destructive' });
      return;
    }
    const err = validateMobile(form.phone, { required: false });
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    const dtErr = validateDateTime(form.appointment_datetime, { required: true });
    if (dtErr) {
      setDatetimeError(dtErr);
      toast({ title: 'Invalid date and time', description: dtErr, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    setDatetimeError('');
    try {
      if (editRow) {
        await api.put(`/reception/appointments/${editRow.id}`, form);
        toast({ title: 'Appointment updated' });
      } else {
        await api.post('/reception/appointments', form);
        toast({ title: 'Appointment scheduled' });
      }
      setOpen(false);
      setEditRow(null);
      setForm(empty());
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      visitor_name: row.visitor_name || '',
      phone: row.phone || '',
      staff_user_id: row.staff_user_id ? String(row.staff_user_id) : '',
      appointment_datetime: row.appointment_datetime ? row.appointment_datetime.slice(0, 16) : '',
      purpose: row.purpose || '',
      status: row.status || 'scheduled',
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete appointment?')) return;
    try {
      await api.delete(`/reception/appointments/${id}`);
      toast({ title: 'Deleted' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div><h1 className="text-2xl font-bold">Appointments</h1><p className="text-muted-foreground">Schedule meetings with staff</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchItems}><RefreshCw className="w-4 h-4" /></Button>
          {canManage && <Button onClick={() => { setEditRow(null); setForm(empty()); setOpen(true); }}><Plus className="w-4 h-4 mr-2" />Schedule</Button>}
        </div>
      </div>
      <Card>
        <CardHeader><CardTitle>Appointments</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-muted-foreground">Loading…</p> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground">
                <th className="py-2">Visitor</th><th className="py-2">Phone</th><th className="py-2">Staff</th>
                <th className="py-2">Date & Time</th><th className="py-2">Status</th><th className="py-2">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-border/50">
                    <td className="py-2 font-medium">{r.visitor_name}</td>
                    <td className="py-2">{r.phone || '—'}</td>
                    <td className="py-2">{staffLabel({ first_name: r.staff_first_name, last_name: r.staff_last_name })}</td>
                    <td className="py-2">{r.appointment_datetime || '—'}</td>
                    <td className="py-2">{labelFor(APPOINTMENT_STATUSES, r.status)}</td>
                    <td className="py-2 flex gap-2">
                      {canManage && <Button size="sm" variant="outline" onClick={() => openEdit(r)}>Edit</Button>}
                      {canDelete && <Button size="sm" variant="destructive" onClick={() => remove(r.id)}>Delete</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editRow ? 'Edit Appointment' : 'Schedule Appointment'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Visitor Name *</Label><Input value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} /></div>
            <div><Label>Phone</Label><MobileInput value={form.phone} onChange={(v) => { setForm({ ...form, phone: v }); setPhoneError(''); }} error={phoneError} /></div>
            <div><Label>Staff Member</Label>
              <Select value={form.staff_user_id || 'none'} onValueChange={(v) => setForm({ ...form, staff_user_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={String(s.id)}>{staffLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date & Time *</Label><DateTimeInput value={form.appointment_datetime} onChange={(v) => { setForm({ ...form, appointment_datetime: v }); setDatetimeError(''); }} error={datetimeError} required /></div>
            <div><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} /></div>
            {editRow && (
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{APPOINTMENT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
